import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import type { Session } from "@supabase/supabase-js";
import ProtectedRoute from "./components/ProtectedRoute";
import Navigation from "./components/Navigation";
import ScrollToTop from "./components/ScrollToTop";
import Dashboard from "./components/Dashboard";
// Standalone routes removed — now served as sub-tabs in ClubOptimizerHub
import UserProfile from "./components/UserProfile";
import UserCaptureForm from "./components/UserCaptureForm";
import AuthModal from "./components/AuthModal";
import ContactForm from "./components/ContactForm";
// BallMatching moved to ClubOptimizerHub sub-tab
import OnboardingWizard, { OnboardingData } from "./components/OnboardingWizard";
import Distances from "./components/Distances";
import Recommendations from "./components/Recommendations";
import BeginnerBuilder from "./components/BeginnerBuilder";
import MyGolfBag from "./components/MyGolfBag";
import ClubOptimizerHub from "./components/ClubOptimizerHub";
import EmailConfirmationScreen from "./components/EmailConfirmationScreen";
import OnboardingWalkthrough from "./components/OnboardingWalkthrough";
import ResetPassword from "./components/ResetPassword";
import About from "./components/About";
import Contact from "./components/Contact";
import Privacy from "./components/Privacy";
import Terms from "./components/Terms";
import PublicBagView from "./components/PublicBagView";
import PartnerInvite from "./components/PartnerInvite";
import Unsubscribe from "./components/Unsubscribe";
import GrowthOps from "./components/GrowthOps";
import DataNotice from "./components/DataNotice";
import BagComparison from "./components/BagComparison";
import ProgressDashboard from "./components/ProgressDashboard";
import AskClubIQ from "./components/AskClubIQ";
import FindFitter from "./components/FindFitter";
import InstallPrompt from "./components/InstallPrompt";
import { UserProfile as UserProfileType, AuthUser, SignUpData, SignInData } from "./types";
import { saveUserProfile, updateClubDistances } from "./utils/storage";
import { supabase } from "./config/supabase";
import { getPersonaFromScore, estimateSwingSpeed, estimateHandicapFromScore } from "./utils/golfPersona";
import { CLUB_DATA } from "./utils/calculations";
import { IRON_SETS, WEDGE_SETS, createClubsFromSet } from "./utils/clubSets";
import * as profileService from "./services/profileService";
import { recordActivationIfEligible, recordProductEvent } from "./services/productEvents";
import { canResumeOnboarding, nextRouteAfterAuth } from "./utils/onboardingRouting";
import { onboardingSkipDatabasePatch, onboardingSkipProfilePatch } from "./utils/onboardingSkip";
import { captureFirstTouchFromLocation, getFirstTouch } from "./utils/firstTouch";
// import { autoCleanupStaleSession } from "./utils/sessionCleanup"; // DISABLED - too aggressive

/** Clear Supabase auth errors so users see what actually failed (wrong password vs network). */
function friendlySignInMessage(message: string): string {
  const t = message.toLowerCase();
  if (
    t.includes("invalid login credentials") ||
    t.includes("invalid email or password") ||
    t.includes("invalid credentials")
  ) {
    return "That email and password don’t match what we have on file. Recheck both fields, watch for accidental spaces in the email, or use Forgot password below.";
  }
  if (t.includes("email not confirmed") || t.includes("confirm your email")) {
    return "Please confirm your email before signing in. Check your inbox for the ClubIQ confirmation link.";
  }
  if (t.includes("too many requests") || t.includes("rate limit")) {
    return "Too many sign-in attempts. Wait a minute and try again.";
  }
  return message;
}

function App() {
  const [userProfile, setUserProfile] = useState<UserProfileType | null>(null);
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCaptureForm, setShowCaptureForm] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<"signin" | "signup">("signup"); // Default to signup
  const [showWelcomeOverlay, setShowWelcomeOverlay] = useState(false); // Start with false for now
  const [showContactForm, setShowContactForm] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [, setHasCompletedOnboarding] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [showProductTour, setShowProductTour] = useState(false);
  const [quizCompleteSummary, setQuizCompleteSummary] = useState<{ clubCount: number; distanceCount: number } | null>(null);

  // Check if user is authenticated
  const isAuthenticated = authUser !== null;

  // Check if user has provided required information
  const hasRequiredInfo = userProfile && !!(userProfile.firstName && userProfile.lastName);

  // Function to handle actions that require user information
  const handleActionRequiringInfo = (action: string) => {
    // Ensure only one modal/form is shown at a time
    setShowAuthModal(false);
    setShowCaptureForm(false);

    if (!isAuthenticated) {
      setPendingAction(action);
      setAuthModalMode("signup"); // Default to signup mode
      setShowAuthModal(true);
      return false; // For DistanceCalculator: don't proceed with calculation
    }

    if (!hasRequiredInfo) {
      setPendingAction(action);
      setShowCaptureForm(true);
      return false; // For DistanceCalculator: don't proceed with calculation
    }

    // If user is authenticated and has info, proceed with the action
    if (action.startsWith('/')) {
      window.location.href = action;
      return; // For Dashboard navigation
    } else {
      // For non-page actions (like calculations), return true to indicate proceed
      return true; // For DistanceCalculator: proceed with calculation
    }
  };

  useEffect(() => {
    captureFirstTouchFromLocation(window.location.search, window.location.pathname);
  }, []);

  // Modal handlers
  const handleOpenAuthModal = (mode: "signin" | "signup") => {
    setShowCaptureForm(false); // Close capture form if open
    setAuthModalMode(mode);
    setShowAuthModal(true);
    setShowWelcomeOverlay(false); // Close welcome overlay when opening auth modal
    if (mode === 'signup') {
      void recordProductEvent('SIGNUP_STARTED');
    }
  };

  const handleCloseAuthModal = () => {
    setShowAuthModal(false);
    setAuthModalMode("signup"); // Always reset to signup mode
  };

  const handleSwitchAuthMode = () => {
    setAuthModalMode(authModalMode === "signup" ? "signin" : "signup");
  };

  // Authentication functions
  const handleSignUp = async (data: SignUpData) => {
    try {
      // Email confirmation is required, so the user has NO session immediately after signUp.
      // RLS will block any subsequent profile/referral queries until they confirm + sign in.
      // We must NOT await profile load or referral RPCs here — that's what makes the
      // "Creating Account..." spinner hang for tens of seconds.

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email.trim().toLowerCase(),
        password: data.password,
        options: {
          data: {
            first_name: data.firstName,
            last_name: data.lastName,
          },
          // Send the user back to clubiq.golf after they click the confirmation link
          // (avoids stale Supabase Site URL defaults / iOS "Open with" chooser ambiguity)
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (authError) {
        throw new Error(authError.message);
      }

      if (!authData.user) {
        throw new Error('Sign up did not return a user. Please try again.');
      }

      console.log('✅ Signup successful for:', authData.user.email);
      console.log('📊 Session after signup:', authData.session ? 'EXISTS' : 'NULL');

      const newAuthUser: AuthUser = {
        id: authData.user.id,
        email: authData.user.email!,
        firstName: data.firstName,
        lastName: data.lastName,
        createdAt: new Date(authData.user.created_at),
        isAuthenticated: true,
      };
      setAuthUser(newAuthUser);

      // Immediately move user to the email-confirmation screen — DO NOT block on DB work.
      setShowAuthModal(false);
      setShowEmailConfirmation(true);

      // Save the referral code (if any) so we can apply it after the user signs in
      // post-confirmation, when they actually have a session and RLS will permit writes.
      if (data.referralCode) {
        try {
          localStorage.setItem(
            `pendingReferral_${authData.user.id}`,
            JSON.stringify({ code: data.referralCode.toUpperCase().trim() })
          );
        } catch {
          // ignore quota / private mode failures
        }
      }

      // Best-effort profile + referral setup in the background.
      // If the project requires email confirmation, these will be no-ops (no session) and
      // run again on the user's first authenticated sign-in inside handleSignIn.
      void (async () => {
        try {
          if (authData.session) {
            await loadUserProfileFromSupabase(authData.user!.id);
            try {
              await profileService.ensureReferralCode(authData.user!.id, data.firstName);
            } catch (refError) {
              console.warn('⚠️ Failed to generate referral code (background):', refError);
            }
            if (data.referralCode) {
              try {
                await profileService.applyReferralCode(data.referralCode, authData.user!.id);
              } catch (refError) {
                console.warn('⚠️ Failed to apply referral code (background):', refError);
              }
            }
          } else {
            console.log('ℹ️ No session yet (email confirmation required); deferring profile/referral setup until first sign-in');
          }
        } catch (err) {
          console.error('⚠️ Background post-signup work failed:', err);
        }
      })();
    } catch (error) {
      console.error('💥 Sign up error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Could not reach the sign-up service. Check your connection and try again.');
    }
  };

  const handleSignIn = async (data: SignInData) => {
    try {
      console.log('🔐 Starting sign in process...');

      // Normalize email only (never trim password — spaces can be intentional)
      const email = data.email.trim().toLowerCase();

      // Do not call signOut() before password sign-in — it can race or stall on mobile Safari
      // and is unnecessary; signInWithPassword replaces the session.

      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password: data.password,
      });

      if (authError) {
        console.error('❌ Auth error:', authError);
        throw new Error(friendlySignInMessage(authError.message || 'Sign in failed'));
      }

      if (!authData.user) {
        throw new Error('Sign in returned no user. Please try again.');
      }

      console.log('✅ User authenticated:', authData.user.email);

      // CRITICAL: Check if email is confirmed
      if (!authData.user.email_confirmed_at) {
        console.log('⚠️ Email not confirmed - showing confirmation screen');

        const authUserPartial: AuthUser = {
          id: authData.user.id,
          email: authData.user.email!,
          firstName: authData.user.user_metadata.first_name || 'User',
          lastName: authData.user.user_metadata.last_name || 'Name',
          createdAt: new Date(authData.user.created_at),
          isAuthenticated: true,
        };

        setAuthUser(authUserPartial);
        setShowAuthModal(false);
        setShowEmailConfirmation(true);
        return;
      }

      console.log('✅ Email confirmed');

      const authUser: AuthUser = {
        id: authData.user.id,
        email: authData.user.email!,
        firstName: authData.user.user_metadata.first_name,
        lastName: authData.user.user_metadata.last_name,
        createdAt: new Date(authData.user.created_at),
        isAuthenticated: true,
      };

      setAuthUser(authUser);

      console.log('✅ Signin completed successfully');

      setShowAuthModal(false);

      void (async () => {
        try {
          await loadUserProfileFromSupabase(authData.user.id);
          console.log('✅ Profile loaded after sign-in');
          // Generate referral code for users that haven't been bootstrapped yet
          // (users who signed up while email confirmation was required will hit this on first sign-in)
          try {
            await profileService.ensureReferralCode(
              authData.user.id,
              authData.user.user_metadata?.first_name || 'User'
            );
          } catch (refErr) {
            console.warn('⚠️ Failed to ensure referral code post sign-in:', refErr);
          }
          // Apply any pending referral code stashed at sign-up time
          try {
            const pendingRaw = localStorage.getItem(`pendingReferral_${authData.user.id}`);
            if (pendingRaw) {
              const pending = JSON.parse(pendingRaw) as { code?: string };
              if (pending?.code) {
                const result = await profileService.applyReferralCode(pending.code, authData.user.id);
                if (result.success) {
                  console.log('✅ Applied pending referral code:', pending.code);
                } else {
                  console.warn('⚠️ Pending referral not applied:', result.error);
                }
              }
              localStorage.removeItem(`pendingReferral_${authData.user.id}`);
            }
          } catch (refErr) {
            console.warn('⚠️ Failed to apply pending referral code:', refErr);
          }
        } catch (err) {
          console.error('⚠️ Profile load failed after sign-in:', err);
        } finally {
          const loaded = await profileService.getProfile(authData.user.id).catch(() => null);
          const destination = nextRouteAfterAuth({
            hasCompletedOnboarding: loaded?.hasCompletedOnboarding,
            onboardingSkippedAt: loaded?.onboardingSkippedAt,
          });
          if (destination.showOnboarding) {
            setShowOnboarding(true);
            void recordProductEvent('ONBOARDING_STARTED');
          }
          void supabase.rpc('record_authenticated_session');
          if (authData.user.email_confirmed_at) {
            void recordProductEvent('EMAIL_CONFIRMED');
          }
          const touch = getFirstTouch();
          void supabase.rpc('claim_first_touch', {
            p_source: touch.source || '',
            p_medium: touch.medium || '',
            p_campaign: touch.campaign || '',
            p_landing: touch.landingPage || window.location.pathname,
            p_first_touch: touch.firstTouchAt || new Date().toISOString(),
          });
          if (touch.partnerCode) {
            void supabase.rpc('apply_partner_attribution', { p_code: touch.partnerCode });
          }
          if (touch.userReferralCode && !localStorage.getItem(`pendingReferral_${authData.user.id}`)) {
            void profileService.applyReferralCode(touch.userReferralCode, authData.user.id);
          }
          window.location.href = destination.path;
        }
      })();

    } catch (error) {
      console.error('💥 Sign in error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(
        'Could not reach the sign-in service. Check your connection and try again.'
      );
    }
  };

  const loadUserProfileFromSupabase = async (userId: string) => {
    console.log('🔍 Loading profile for user ID:', userId);

    // Add a small delay to prevent race conditions
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      // CRITICAL FIX: Check localStorage FIRST before hitting the database
      // This prevents data loss during loading issues
      console.log('🔍 Step 1: Checking localStorage backups FIRST...');
      const backupKeys = [
        `PERSISTENT_PROFILE_${userId}`,
        `quizBackup_${userId}`,
        `onboarding_${userId}`,
        `emergency_backup_${userId}`,
        'clubiq-user-profile' // Standard storage key
      ];

      let localBackupProfile: UserProfileType | null = null;
      
      for (const key of backupKeys) {
        const backupRaw = localStorage.getItem(key);
        if (backupRaw) {
          try {
            const parsed = JSON.parse(backupRaw);
            // Check if this backup belongs to the current user
            if (parsed.id === userId || parsed.userId === userId || (!parsed.id && key === 'clubiq-user-profile')) {
              console.log(`🔍 Found localStorage backup in ${key}`);
              
              // Reconstruct profile from backup
              localBackupProfile = {
                id: userId,
                firstName: parsed.firstName || parsed.profile?.firstName || 'User',
                lastName: parsed.lastName || parsed.profile?.lastName || 'Name',
                email: parsed.email || parsed.profile?.email || '',
                handicap: parsed.handicap ?? parsed.profile?.handicap ?? 18,
                height: parsed.height ?? parsed.profile?.height ?? 70,
                weight: parsed.weight ?? parsed.profile?.weight ?? 180,
                age: parsed.age ?? parsed.profile?.age ?? 35,
                swingSpeed: parsed.swingSpeed || parsed.profile?.swingSpeed,
                swingSpeedRange: parsed.swingSpeedRange || parsed.profile?.swingSpeedRange,
                swingTempo: parsed.swingTempo || parsed.profile?.swingTempo || 'medium',
                ballFlight: parsed.ballFlight || parsed.profile?.ballFlight || 'straight',
                skillLevel: parsed.skillLevel || parsed.profile?.skillLevel || 'intermediate',
                playingExperience: parsed.playingExperience || parsed.profile?.playingExperience || 'casual',
                courseFrequency: parsed.courseFrequency || parsed.profile?.courseFrequency || 'monthly',
                missPattern: parsed.missPattern || parsed.profile?.missPattern,
                preferredCourseType: parsed.preferredCourseType || parsed.profile?.preferredCourseType,
                golfGoals: parsed.golfGoals || parsed.profile?.golfGoals || [],
                currentIssues: parsed.currentIssues || parsed.profile?.currentIssues || [],
                clubDistances: parsed.clubDistances || parsed.profile?.clubDistances || [],
                golfBag: parsed.golfBag || parsed.profile?.golfBag || [],
                preferredUnits: parsed.preferredUnits || parsed.profile?.preferredUnits || 'yards',
                hasCompletedOnboarding: parsed.hasCompletedOnboarding ?? parsed.profile?.hasCompletedOnboarding ?? false,
                averageScore: parsed.averageScore || parsed.profile?.averageScore,
                golfPersona: parsed.golfPersona || parsed.profile?.golfPersona,
                createdAt: parsed.createdAt ? new Date(parsed.createdAt) : new Date(),
                updatedAt: parsed.updatedAt ? new Date(parsed.updatedAt) : new Date(),
              };
              
              console.log('🔄 Recovered profile from localStorage:', {
                golfBagLength: localBackupProfile.golfBag.length,
                hasCompletedOnboarding: localBackupProfile.hasCompletedOnboarding,
                source: key
              });
              break;
            }
          } catch (e) {
            console.error(`Failed to parse backup ${key}:`, e);
          }
        }
      }
      
      // If we found a local backup, use it immediately and sync to DB in background
      if (localBackupProfile) {
        console.log('✅ Using localStorage profile immediately');
        
        // Ensure putter exists in localStorage-loaded profiles too
        if (localBackupProfile.golfBag && localBackupProfile.golfBag.length > 0 && !localBackupProfile.golfBag.some((club: any) => club.type === 'putter')) {
          console.log('⚠️ No putter found in localStorage profile - adding one automatically');
          localBackupProfile.golfBag.push({
            id: `auto-putter-localStorage-${Date.now()}`,
            type: 'putter',
            brand: "",
            model: "",
            shaftType: 'steel',
            shaftFlex: 'regular',
            lie: 70,
            loft: 3,
            length: 34,
            isInBag: true,
            distance: undefined,
            isDistanceEstimated: true,
            notes: 'Added automatically - every golfer needs a putter!'
          });
        }
        
        setUserProfile(localBackupProfile);
        saveUserProfile(localBackupProfile);
        
        // Background sync to Supabase (don't await, don't block)
        profileService.upsertProfile(localBackupProfile).then(() => {
          console.log('✅ Background sync to Supabase completed');
        }).catch(err => {
          console.error('⚠️ Background sync failed (non-critical):', err);
        });
        
        return localBackupProfile;
      }
      
      // No local backup found, try fetching from Supabase
      console.log('🔍 Step 2: No localStorage backup, fetching from Supabase...');
      const userProfile = await profileService.getProfile(userId);

      if (!userProfile) {
        console.warn('⚠️ Profile not found in Supabase either - creating basic profile');
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          try {
            const basicProfile: UserProfileType = {
              id: userId,
              firstName: session.user.user_metadata?.first_name || 'User',
              lastName: session.user.user_metadata?.last_name || 'Name',
              email: session.user.email || '',
              handicap: 18,
              height: 70,
              weight: 180,
              age: 35,
              swingSpeed: undefined,
              swingSpeedRange: undefined,
              swingTempo: 'medium',
              ballFlight: 'straight',
              skillLevel: 'intermediate',
              playingExperience: 'casual',
              courseFrequency: 'monthly',
              missPattern: undefined,
              preferredCourseType: undefined,
              golfGoals: [],
              currentIssues: [],
              clubDistances: [],
              golfBag: [],
              preferredUnits: 'yards',
              hasCompletedOnboarding: false,
              createdAt: new Date(),
              updatedAt: new Date(),
            };
            
            const createdProfile = await profileService.upsertProfile(basicProfile);
            console.log('✅ Created basic profile:', createdProfile.id);
            setUserProfile(createdProfile);
            saveUserProfile(createdProfile);
            return createdProfile;
          } catch (createError) {
            console.error('❌ Failed to create basic profile:', createError);
            return null;
          }
        }
        
        return null;
      }

      // Ensure every profile has a putter (for existing profiles)
      if (userProfile.golfBag && userProfile.golfBag.length > 0 && !userProfile.golfBag.some((club: any) => club.type === 'putter')) {
        console.log('⚠️ No putter found in Supabase profile - adding one automatically');
        userProfile.golfBag.push({
          id: `auto-putter-${Date.now()}`,
          type: 'putter',
          brand: "",
          model: "",
          shaftType: 'steel',
          shaftFlex: 'regular',
          lie: 70,
          loft: 3,
          length: 34,
          isInBag: true,
          distance: undefined,
          isDistanceEstimated: true,
          notes: 'Added automatically - every golfer needs a putter!'
        });
      }

      console.log('💾 Setting profile in state:', {
        id: userProfile.id,
        hasCompletedOnboarding: userProfile.hasCompletedOnboarding,
        golfBagLength: userProfile.golfBag?.length || 0,
        clubDistancesLength: userProfile.clubDistances?.length || 0,
        firstName: userProfile.firstName,
        email: userProfile.email,
        height: userProfile.height,
        weight: userProfile.weight
      });

      // Check for data integrity issues and attempt recovery (silent)
      // ENHANCED: Check BOTH empty golf bag AND hasCompletedOnboarding mismatch
      // Reuse backupKeys from earlier in the function
      
      // Check if localStorage indicates completed onboarding
      let hasLocalBackupWithData = false;
      let localBackupData: any = null;
      
      for (const key of backupKeys) {
        const backupRaw = localStorage.getItem(key);
        if (backupRaw) {
          try {
            const parsed = JSON.parse(backupRaw);
            if (parsed.golfBag && parsed.golfBag.length > 0) {
              hasLocalBackupWithData = true;
              localBackupData = parsed;
              console.log(`🔍 Found localStorage backup in ${key} with ${parsed.golfBag.length} clubs`);
              break;
            }
          } catch (e) {
            console.error(`Failed to parse ${key}:`, e);
          }
        }
      }

      // Determine if recovery is needed
      const needsRecovery = 
        (!userProfile.golfBag || userProfile.golfBag.length === 0) || // Empty golf bag
        (!userProfile.hasCompletedOnboarding && hasLocalBackupWithData); // DB shows incomplete but localStorage has data

      console.log('🔍 Recovery check:', {
        golfBagLength: userProfile.golfBag?.length || 0,
        hasCompletedOnboarding: userProfile.hasCompletedOnboarding,
        hasLocalBackupWithData,
        needsRecovery
      });

      if (needsRecovery && hasLocalBackupWithData && localBackupData) {
        console.log('🔄 CRITICAL: Data mismatch detected - attempting recovery from localStorage');

        try {
          // Update the profile with backup data
          const recoveredProfile: UserProfileType = {
            ...userProfile,
            golfBag: localBackupData.golfBag,
            clubDistances: localBackupData.clubDistances || [],
            height: localBackupData.height || userProfile.height,
            weight: localBackupData.weight || userProfile.weight,
            age: localBackupData.age || userProfile.age,
            averageScore: localBackupData.averageScore || userProfile.averageScore,
            ballFlight: localBackupData.ballFlight || userProfile.ballFlight,
            swingTempo: localBackupData.swingTempo || userProfile.swingTempo,
            skillLevel: localBackupData.skillLevel || userProfile.skillLevel,
            playingExperience: localBackupData.playingExperience || userProfile.playingExperience,
            courseFrequency: localBackupData.courseFrequency || userProfile.courseFrequency,
            hasCompletedOnboarding: true, // Force to true since we have quiz data
          };

          // Ensure putter exists in recovered profile
          if (recoveredProfile.golfBag && recoveredProfile.golfBag.length > 0 && !recoveredProfile.golfBag.some((club: any) => club.type === 'putter')) {
            console.log('⚠️ No putter in recovered profile - adding one automatically');
            recoveredProfile.golfBag.push({
              id: `auto-putter-recovered-${Date.now()}`,
              type: 'putter',
              brand: "",
              model: "",
              shaftType: 'steel',
              shaftFlex: 'regular',
              lie: 70,
              loft: 3,
              length: 34,
              isInBag: true,
              distance: undefined,
              isDistanceEstimated: true,
              notes: 'Added automatically - every golfer needs a putter!'
            });
          }

          console.log('💾 Saving recovered data to Supabase...', {
            golfBagLength: recoveredProfile.golfBag.length,
            clubDistancesLength: recoveredProfile.clubDistances.length,
            hasCompletedOnboarding: true
          });

          await handleProfileUpdate(recoveredProfile);

          // Update local state
          setUserProfile(recoveredProfile);
          saveUserProfile(recoveredProfile);

          console.log('✅ Data recovery successful!');
        } catch (e) {
          console.error(`❌ Recovery failed:`, e);
        }
      }

      if (userProfile.hasCompletedOnboarding && (!userProfile.clubDistances || userProfile.clubDistances.length === 0)) {
        console.log('ℹ️ Profile shows completed onboarding but club distances are empty');
      }

      setUserProfile(userProfile);
      saveUserProfile(userProfile); // Also save to localStorage as backup
      return userProfile;
    } catch (error) {
      console.error('💥 Error loading user profile:', error);
      // Silent error handling - no user alerts
      return null;
    }
  };

  const syncLocalStorageToSupabase = async (userId: string, session?: Session | null) => {
    console.log('🔄 Checking for localStorage backups to sync...');

    const backupKeys = [
      `PERSISTENT_PROFILE_${userId}`,
      `quizBackup_${userId}`,
      `onboarding_${userId}`,
    ];

    let backupRecord: { key: string; data: any } | null = null;

    for (const key of backupKeys) {
      try {
        const backupDataRaw = localStorage.getItem(key);
        if (!backupDataRaw) {
          continue;
        }

        const parsed = JSON.parse(backupDataRaw);
        if (parsed && Array.isArray(parsed.golfBag) && parsed.golfBag.length > 0) {
          backupRecord = { key, data: parsed };
          console.log(`📤 Found unsynced data in ${key}`);
          break;
        }
      } catch (error) {
        console.error(`❌ Failed to parse backup in ${key}:`, error);
      }
    }

    if (!backupRecord) {
      console.log('ℹ️ No unsynced data found in localStorage');
      return;
    }

    const activeSession = session ?? (await supabase.auth.getSession()).data.session ?? null;
    const sessionUser = activeSession?.user;

    let existingProfile: UserProfileType | null = null;
    try {
      existingProfile = await profileService.getProfile(userId);
    } catch (error) {
      console.warn('⚠️ Unable to load current profile during sync (will rebuild from backup):', error);
    }

    const backupData = backupRecord.data;

    const mergedProfile: UserProfileType = {
      id: userId,
      firstName:
        backupData.firstName || existingProfile?.firstName || userProfile?.firstName || sessionUser?.user_metadata?.first_name || 'Golfer',
      lastName:
        backupData.lastName || existingProfile?.lastName || userProfile?.lastName || sessionUser?.user_metadata?.last_name || 'Member',
      email:
        backupData.email || existingProfile?.email || userProfile?.email || sessionUser?.email || '',
      handicap:
        backupData.handicap ?? existingProfile?.handicap ?? userProfile?.handicap ?? 18,
      height:
        backupData.height ?? existingProfile?.height ?? userProfile?.height ?? 70,
      weight:
        backupData.weight ?? existingProfile?.weight ?? userProfile?.weight ?? 180,
      birthdate: existingProfile?.birthdate || userProfile?.birthdate,
      age: backupData.age ?? existingProfile?.age ?? userProfile?.age ?? 35,
      averageScore: backupData.averageScore ?? existingProfile?.averageScore ?? userProfile?.averageScore,
      golfPersona: backupData.golfPersona ?? existingProfile?.golfPersona ?? userProfile?.golfPersona,
      swingSpeed: backupData.swingSpeed ?? existingProfile?.swingSpeed ?? userProfile?.swingSpeed,
      swingSpeedRange:
        backupData.swingSpeedRange ?? existingProfile?.swingSpeedRange ?? userProfile?.swingSpeedRange,
      swingTempo:
        backupData.swingTempo ?? existingProfile?.swingTempo ?? userProfile?.swingTempo ?? 'medium',
      ballFlight:
        backupData.ballFlight ?? existingProfile?.ballFlight ?? userProfile?.ballFlight ?? 'straight',
      handSize: existingProfile?.handSize ?? userProfile?.handSize,
      gripType: existingProfile?.gripType ?? userProfile?.gripType,
      gripPressure: existingProfile?.gripPressure ?? userProfile?.gripPressure,
      attackAngle: existingProfile?.attackAngle ?? userProfile?.attackAngle,
      skillLevel:
        backupData.skillLevel ?? existingProfile?.skillLevel ?? userProfile?.skillLevel ?? 'intermediate',
      playingExperience:
        backupData.playingExperience ?? existingProfile?.playingExperience ?? userProfile?.playingExperience ?? 'casual',
      courseFrequency:
        backupData.courseFrequency ?? existingProfile?.courseFrequency ?? userProfile?.courseFrequency ?? 'monthly',
      missPattern: existingProfile?.missPattern ?? userProfile?.missPattern,
      preferredCourseType:
        existingProfile?.preferredCourseType ?? userProfile?.preferredCourseType,
      golfGoals:
        backupData.golfGoals ?? existingProfile?.golfGoals ?? userProfile?.golfGoals ?? [],
      currentIssues:
        backupData.currentIssues ?? existingProfile?.currentIssues ?? userProfile?.currentIssues ?? [],
      clubDistances:
        backupData.clubDistances ?? existingProfile?.clubDistances ?? userProfile?.clubDistances ?? [],
      golfBag:
        backupData.golfBag ?? existingProfile?.golfBag ?? userProfile?.golfBag ?? [],
      preferredUnits:
        backupData.preferredUnits ?? existingProfile?.preferredUnits ?? userProfile?.preferredUnits ?? 'yards',
      hasCompletedOnboarding: true,
      createdAt: existingProfile?.createdAt ?? userProfile?.createdAt ?? new Date(),
      updatedAt: new Date(),
    };

    // Ensure putter exists in merged profile before syncing
    if (mergedProfile.golfBag && mergedProfile.golfBag.length > 0 && !mergedProfile.golfBag.some((club: any) => club.type === 'putter')) {
      console.log('⚠️ No putter in merged profile - adding one automatically');
      mergedProfile.golfBag.push({
        id: `auto-putter-sync-${Date.now()}`,
        type: 'putter',
        brand: "",
        model: "",
        shaftType: 'steel',
        shaftFlex: 'regular',
        lie: 70,
        loft: 3,
        length: 34,
        isInBag: true,
        distance: undefined,
        isDistanceEstimated: true,
        notes: 'Added automatically - every golfer needs a putter!'
      });
    }

    if (!mergedProfile.email) {
      console.error('❌ Cannot sync profile - missing email address');
      return;
    }

    try {
      console.log('💾 Syncing recovered data to Supabase...');
      const savedProfile = await profileService.upsertProfile(mergedProfile);

      setUserProfile(savedProfile);
      saveUserProfile(savedProfile);

      const refreshedBackup = {
        ...backupData,
        golfBag: savedProfile.golfBag,
        clubDistances: savedProfile.clubDistances,
        firstName: savedProfile.firstName,
        lastName: savedProfile.lastName,
        email: savedProfile.email,
        handicap: savedProfile.handicap,
        height: savedProfile.height,
        weight: savedProfile.weight,
        age: savedProfile.age,
        averageScore: savedProfile.averageScore,
        golfPersona: savedProfile.golfPersona,
        golfGoals: savedProfile.golfGoals,
        currentIssues: savedProfile.currentIssues,
        swingTempo: savedProfile.swingTempo,
        swingSpeed: savedProfile.swingSpeed,
        swingSpeedRange: savedProfile.swingSpeedRange,
        ballFlight: savedProfile.ballFlight,
        skillLevel: savedProfile.skillLevel,
        playingExperience: savedProfile.playingExperience,
        courseFrequency: savedProfile.courseFrequency,
        preferredUnits: savedProfile.preferredUnits,
        savedAt: new Date().toISOString(),
      };

      localStorage.setItem(backupRecord.key, JSON.stringify(refreshedBackup));

      console.log('✅ Sync complete! Data saved to Supabase.');
    } catch (error) {
      console.error('❌ Failed to sync recovered data to Supabase:', error);
    }
  };

  const handleSignOut = async () => {
    console.log('🔄 handleSignOut called - Starting sign out process...');

    // CRITICAL: Save current profile data before clearing storage
    if (userProfile && authUser) {
      const backupKey = `LOGOUT_BACKUP_${authUser.id}_${Date.now()}`;
      try {
        localStorage.setItem(backupKey, JSON.stringify({
          userId: authUser.id,
          email: authUser.email,
          golfBag: userProfile.golfBag,
          clubDistances: userProfile.clubDistances,
          profile: userProfile,
          savedAt: new Date().toISOString()
        }));
        console.log('💾 Profile backed up before logout');
      } catch (e) {
        console.error('❌ Failed to backup before logout:', e);
      }
    }

    try {
      // First, sign out from Supabase Auth (this removes auth tokens)
      console.log('🚪 Signing out from Supabase Auth...');
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('⚠️ Supabase sign out error:', error);
      } else {
        console.log('✅ Supabase sign out successful');
      }
    } catch (error) {
      console.error('💥 Sign out error:', error);
    }

    // Clear all authentication state
    console.log('🧹 Clearing all authentication state...');
    setAuthUser(null);
    setUserProfile(null);
    setShowCaptureForm(false);
    setPendingAction(null);
    setShowAuthModal(false);
    setAuthModalMode("signup");

    // Clear browser storage EXCEPT persistent backups
    console.log('💾 Clearing storage (preserving backups)...');
    
    // Save all backup keys first
    const backupKeys: {[key: string]: string | null} = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('PERSISTENT_PROFILE_') || key.includes('quizBackup_') || key.includes('onboarding_') || key.includes('LOGOUT_BACKUP_'))) {
        backupKeys[key] = localStorage.getItem(key);
      }
    }
    
    // Clear everything
    localStorage.clear();
    sessionStorage.clear();
    
    // Restore backups
    Object.entries(backupKeys).forEach(([key, value]) => {
      if (value) {
        localStorage.setItem(key, value);
      }
    });
    
    console.log(`💾 Preserved ${Object.keys(backupKeys).length} backup(s)`);
    
    // Also clear IndexedDB (where Supabase stores auth data)
    try {
      const databases = await window.indexedDB.databases();
      databases.forEach(db => {
        if (db.name) {
          console.log(`🗑️ Deleting IndexedDB: ${db.name}`);
          window.indexedDB.deleteDatabase(db.name);
        }
      });
    } catch (error) {
      console.log('⚠️ Could not clear IndexedDB (non-critical):', error);
    }

    console.log('✅ All cleanup completed, redirecting to home...');

    // Force redirect to home with full page reload
    setTimeout(() => {
      console.log('🔄 Forcing page reload...');
      window.location.href = '/';
    }, 100);
  };

  const handleOnboardingComplete = async (onboardingData: OnboardingData) => {
    console.log('🎯 Enhanced Onboarding completed with data:', onboardingData);
    console.log('📊 Data breakdown:', {
      averageScore: onboardingData.averageScore,
      sevenIronDistance: onboardingData.sevenIronDistance,
      selectedClubs: onboardingData.selectedClubs,
      selectedClubsLength: onboardingData.selectedClubs?.length || 0,
      starterSetId: onboardingData.starterSetId,
      hasExistingClubs: onboardingData.hasExistingClubs
    });
    
    // Get persona from score
    const persona = onboardingData.averageScore 
      ? getPersonaFromScore(onboardingData.averageScore)
      : null;
    
    console.log('🎭 Persona calculated:', persona?.name, 'HC:', persona?.estimatedHandicap);
    
    // Build golf bag from selected sets OR starter set
    let golfBag: any[] = [];
    console.log('🏌️ Building golf bag...');
    
    // NEW: Handle set-based selection
    // Fix Bug 1: Check explicitly for 'yes' or defined values, not just truthiness
    const hasAnyEquipment = 
      onboardingData.hasDriver === 'yes' ||
      (Array.isArray(onboardingData.hasWoods) && onboardingData.hasWoods.length > 0 && !onboardingData.hasWoods.includes('none')) ||
      (Array.isArray(onboardingData.hasHybrids) && onboardingData.hasHybrids.length > 0 && !onboardingData.hasHybrids.includes('none')) ||
      onboardingData.selectedIronSet ||
      (Array.isArray(onboardingData.selectedWedgeLofts) && onboardingData.selectedWedgeLofts.length > 0) ||
      onboardingData.selectedWedgeSet;
    
    if (hasAnyEquipment) {
      console.log('✅ Using new set-based selection');
      
      // Fix Bug 2: Use incrementing counter to ensure unique IDs
      let idCounter = 0;
      
      // Add driver if selected
      if (onboardingData.hasDriver === 'yes') {
        golfBag.push({
          id: `onboarding-driver-${idCounter++}`,
          type: 'driver',
          brand: "",
          model: "",
          shaftType: 'graphite',
          shaftFlex: 'regular',
          lie: 60,
          loft: 10.5,
          length: 45,
          isInBag: true,
          distance: undefined,
          isDistanceEstimated: true,
          notes: 'Added during onboarding - please add brand/model details'
        });
      }
      
      // Add woods based on multi-select array
      if (Array.isArray(onboardingData.hasWoods) && onboardingData.hasWoods.length > 0) {
        const woodLoftMap: {[key: string]: number} = {
          '3w': 15,
          '4w': 16.5,
          '5w': 18,
          '7w': 21,
          '9w': 24
        };
        
        onboardingData.hasWoods.filter(w => w !== 'none').forEach(woodType => {
          const clubType = woodType === '3w' ? '3-wood' :
                          woodType === '5w' ? '5-wood' :
                          woodType === '7w' ? '7-wood' : '3-wood';
          
          golfBag.push({
            id: `onboarding-${clubType}-${idCounter++}`,
            type: clubType as any,
            brand: "",
            model: "",
            shaftType: 'graphite',
            shaftFlex: 'regular',
            lie: 60,
            loft: woodLoftMap[woodType] || 18,
            length: 43,
            isInBag: true,
            distance: undefined,
            isDistanceEstimated: true,
            notes: 'Added during onboarding'
          });
        });
      }
      
      // Add hybrids based on multi-select array
      if (Array.isArray(onboardingData.hasHybrids) && onboardingData.hasHybrids.length > 0) {
        const hybridLoftMap: {[key: string]: number} = {
          '2h': 17,
          '3h': 19,
          '4h': 22,
          '5h': 25,
          '6h': 28,
          '7h': 31
        };
        
        const hybridTypeMap: {[key: string]: string} = {
          '2h': '2-hybrid',
          '3h': '3-hybrid',
          '4h': '4-hybrid',
          '5h': '5-hybrid',
          '6h': '6-hybrid',
          '7h': '7-hybrid'
        };
        
        onboardingData.hasHybrids.filter(h => h !== 'none').forEach(hybridType => {
          const clubType = hybridTypeMap[hybridType];
          if (clubType) {
            golfBag.push({
              id: `onboarding-${clubType}-${idCounter++}`,
              type: clubType as any,
              brand: "",
              model: "",
              shaftType: 'graphite',
              shaftFlex: 'regular',
              lie: 60,
              loft: hybridLoftMap[hybridType] || 22,
              length: 40,
              isInBag: true,
              distance: undefined,
              isDistanceEstimated: true,
              notes: 'Added during onboarding'
            });
          }
        });
      }
      
      // Add iron set
      if (onboardingData.selectedIronSet) {
        const ironSetConfig = IRON_SETS.find(s => s.id === onboardingData.selectedIronSet);
        if (ironSetConfig) {
          const ironClubs = createClubsFromSet(ironSetConfig);
          golfBag = [...golfBag, ...ironClubs];
          console.log(`✅ Added ${ironClubs.length} irons from ${ironSetConfig.name} set`);
        }
      }
      
      // Add wedges based on selected lofts (loft-based selection)
      if (Array.isArray(onboardingData.selectedWedgeLofts) && onboardingData.selectedWedgeLofts.length > 0) {
        const loftToTypeMap: {[key: number]: string} = {
          48: 'pw',
          50: 'aw',
          52: 'gw',
          54: 'gw',
          56: 'sw',
          58: 'lw',
          60: 'lw',
          62: 'lw'
        };
        
        onboardingData.selectedWedgeLofts.filter(l => l !== 'none' as any).forEach(loft => {
          const wedgeType = loftToTypeMap[loft as number] || 'sw';
          
          golfBag.push({
            id: `onboarding-wedge-${loft}-${idCounter++}`,
            type: wedgeType as any,
            brand: "",
            model: "",
            shaftType: 'steel',
            shaftFlex: 'regular',
            lie: 64,
            loft: loft as number,
            length: 35,
            isInBag: true,
            distance: undefined,
            isDistanceEstimated: true,
            notes: `${loft}° wedge added during onboarding`
          });
        });
        console.log(`✅ Added ${onboardingData.selectedWedgeLofts.length} wedges by loft`);
      }
      // Legacy: Handle old wedge set selection for backward compatibility
      else if (onboardingData.selectedWedgeSet) {
        const wedgeSetConfig = WEDGE_SETS.find(s => s.id === onboardingData.selectedWedgeSet);
        if (wedgeSetConfig) {
          const wedgeClubs = createClubsFromSet(wedgeSetConfig);
          golfBag = [...golfBag, ...wedgeClubs];
          console.log(`✅ Added ${wedgeClubs.length} wedges from ${wedgeSetConfig.name} set`);
        }
      }
      
      // Add putter (everyone needs one!)
      golfBag.push({
        id: `onboarding-putter-${idCounter++}`,
        type: 'putter',
        brand: "",
        model: "",
        shaftType: 'steel',
        shaftFlex: 'regular',
        lie: 70,
        loft: 3,
        length: 34,
        isInBag: true,
        distance: undefined,
        isDistanceEstimated: true,
        notes: 'Added during onboarding'
      });
    } 
    // LEGACY: Handle old individual club selection (backward compatibility)
    else if (onboardingData.selectedClubs && onboardingData.selectedClubs.length > 0) {
      console.log('⚠️ Using legacy individual club selection');
      golfBag = onboardingData.selectedClubs.map((clubType, index) => ({
        id: `onboarding-${clubType}-${index}`,
        type: clubType,
        brand: "",
        model: "",
        shaftType: clubType.includes('wood') || clubType.includes('hybrid') || clubType === 'driver' ? 'graphite' : 'steel',
        shaftFlex: 'regular',
        lie: 60,
        loft: (CLUB_DATA as any)[clubType]?.loft || 10,
        length: 36,
        isInBag: true,
        distance: undefined,
        isDistanceEstimated: true,
        notes: 'Added during onboarding - please add brand/model details'
      }));
      
      // Ensure putter is included (every golfer needs one!)
      if (!golfBag.some(club => club.type === 'putter')) {
        golfBag.push({
          id: `onboarding-putter-legacy-${Date.now()}`,
          type: 'putter',
          brand: "",
          model: "",
          shaftType: 'steel',
          shaftFlex: 'regular',
          lie: 70,
          loft: 3,
          length: 34,
          isInBag: true,
          distance: undefined,
          isDistanceEstimated: true,
          notes: 'Added during onboarding'
        });
      }
    } else if (onboardingData.starterSetId) {
      // TODO: Load starter set clubs based on starterSetId
      // For now, create a basic set
      const basicClubs = ['driver', '5-wood', '6-iron', '7-iron', '8-iron', '9-iron', 'pw', 'sw', 'putter'];
      golfBag = basicClubs.map((clubType, index) => ({
        id: `starter-${clubType}-${index}`,
        type: clubType,
        brand: "", // User will select brand
        model: "", // User will select model
        shaftType: clubType.includes('wood') || clubType === 'driver' ? 'graphite' : 'steel',
        shaftFlex: 'regular',
        lie: 60,
        loft: (CLUB_DATA as any)[clubType]?.loft || 10,
        length: 36,
        isInBag: true,
        distance: undefined,
        isDistanceEstimated: true,
        notes: `From ${onboardingData.starterSetId} tier starter set`
      }));
    }
    
    // SAFETY: Always ensure every golfer has a putter (40% of shots!)
    if (!golfBag.some(club => club.type === 'putter')) {
      console.log('⚠️ No putter found, adding one automatically');
      golfBag.push({
        id: `onboarding-putter-auto-${Date.now()}`,
        type: 'putter',
        brand: "",
        model: "",
        shaftType: 'steel',
        shaftFlex: 'regular',
        lie: 70,
        loft: 3,
        length: 34,
        isInBag: true,
        distance: undefined,
        isDistanceEstimated: true,
        notes: 'Added automatically - every golfer needs a putter!'
      });
    }
    
    console.log('✅ Golf bag built:', golfBag.length, 'clubs');
    console.log('Golf bag sample:', golfBag[0]);
    
    // Calculate club distances from 7-iron baseline if provided
    let clubDistances: any[] = [];
    const distanceMap: Record<string, number> = {};
    
    if (onboardingData.sevenIronDistance) {
      const baseDistance = onboardingData.sevenIronDistance;
      const calculatedDistances = {
        'driver': baseDistance * 1.7,
        '3-wood': baseDistance * 1.5,
        '5-wood': baseDistance * 1.35,
        '7-wood': baseDistance * 1.25,
        '2-hybrid': baseDistance * 1.35,
        '3-hybrid': baseDistance * 1.3,
        '4-hybrid': baseDistance * 1.25,
        '5-hybrid': baseDistance * 1.2,
        '6-hybrid': baseDistance * 1.15,
        '7-hybrid': baseDistance * 1.1,
        '3-iron': baseDistance * 1.27,
        '4-iron': baseDistance * 1.2,
        '5-iron': baseDistance * 1.15,
        '6-iron': baseDistance * 1.07,
        '7-iron': baseDistance,
        '8-iron': baseDistance * 0.93,
        '9-iron': baseDistance * 0.85,
        'pw': baseDistance * 0.75,
        'aw': baseDistance * 0.70,
        'gw': baseDistance * 0.65,
        'sw': baseDistance * 0.55,
        'lw': baseDistance * 0.45,
      };
      
      // Store in distance map for lookup
      Object.assign(distanceMap, calculatedDistances);
      
      // Create clubDistances array
      clubDistances = Object.entries(calculatedDistances).map(([club, distance]) => ({
        club,
        distance: Math.round(distance),
        isManuallySet: false
      }));
      
      console.log('✅ Club distances calculated:', clubDistances.length, 'distances');
      console.log('📏 Distance map:', distanceMap);
    } else {
      console.log('⚠️ NO sevenIronDistance provided - distances will be empty!');
    }
    
    // ✅ CRITICAL FIX: Apply distances to each club in golf bag!
    if (Object.keys(distanceMap).length > 0) {
      golfBag = golfBag.map(club => {
        const calculatedDistance = distanceMap[club.type];
        if (calculatedDistance) {
          return {
            ...club,
            distance: Math.round(calculatedDistance),
            isDistanceEstimated: true,
            isManuallySet: false  // These are calculated, not manually entered
          };
        }
        return club;
      });
      console.log('✅ Distances applied to golf bag!', golfBag.filter(c => c.distance).length, 'clubs have distances');
    }
    
    // Ensure we have a valid session and enforce profile id from session for RLS
    const { data: { session: onboardingSession } } = await supabase.auth.getSession();
    const enforcedUserId = onboardingSession?.user?.id || authUser?.id;
    if (!enforcedUserId) {
      console.error('❌ No authenticated user id available during onboarding save');
      // Silent error handling - no user alerts
      return;
    }

    // Create or update the user profile
    const baseProfile = userProfile || {
      id: enforcedUserId,
      firstName: authUser?.firstName || "",
      lastName: authUser?.lastName || "",
      email: authUser?.email || "",
      handicap: persona?.estimatedHandicap || 18,
      height: ((onboardingData.heightFeet || 5) * 12) + (onboardingData.heightInches || 10),
      weight: onboardingData.weight || 180,
      birthdate: onboardingData.birthYear && onboardingData.birthMonth && onboardingData.birthDay
        ? `${onboardingData.birthYear}-${String(onboardingData.birthMonth).padStart(2, '0')}-${String(onboardingData.birthDay).padStart(2, '0')}`
        : undefined,
      age: onboardingData.age || 35,
      swingSpeed: undefined,
      swingSpeedRange: undefined,
      missPattern: onboardingData.mostCommonMiss as any,
      preferredCourseType: undefined,
      golfGoals: onboardingData.primaryGoal ? [onboardingData.primaryGoal] : [],
      currentIssues: onboardingData.biggestChallenge ? [onboardingData.biggestChallenge] : [],
      clubDistances: clubDistances,
      golfBag: golfBag,
      preferredUnits: "yards" as const,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const updatedProfile: UserProfileType = {
      ...baseProfile,
      id: enforcedUserId,
      // Enhanced Onboarding Data
      averageScore: onboardingData.averageScore,
      golfPersona: persona?.id,
      height: ((onboardingData.heightFeet || 5) * 12) + (onboardingData.heightInches || 10),
      weight: onboardingData.weight || baseProfile.weight || 180,
      birthdate: onboardingData.birthYear && onboardingData.birthMonth && onboardingData.birthDay
        ? `${onboardingData.birthYear}-${String(onboardingData.birthMonth).padStart(2, '0')}-${String(onboardingData.birthDay).padStart(2, '0')}`
        : undefined,
      age: onboardingData.age || 35,
      swingSpeed: persona && onboardingData.averageScore 
        ? estimateSwingSpeed(onboardingData.averageScore, onboardingData.age || 35)
        : undefined,
      // Calculate swingSpeedRange from swingSpeed
      swingSpeedRange: persona && onboardingData.averageScore 
        ? (() => {
            const speed = estimateSwingSpeed(onboardingData.averageScore, onboardingData.age || 35);
            if (speed < 85) return 'under-85' as const;
            if (speed < 95) return '85-95' as const;
            if (speed < 105) return '95-105' as const;
            return 'over-105' as const;
          })()
        : undefined,
      ballFlight: onboardingData.ballFlight,
      swingTempo: onboardingData.swingTempo,
      skillLevel: persona ? persona.skillLevel as any :
                 (onboardingData.playingExperience === "beginner" ? "beginner" : 
                  onboardingData.playingExperience === "serious" ? "advanced" : "intermediate"),
      playingExperience: onboardingData.playingExperience,
      courseFrequency: onboardingData.courseFrequency,
      missPattern: onboardingData.mostCommonMiss as any,
      golfGoals: onboardingData.primaryGoal ? [onboardingData.primaryGoal] : [],
      currentIssues: onboardingData.biggestChallenge ? [onboardingData.biggestChallenge, ...onboardingData.mainChallenges] : onboardingData.mainChallenges,
      handicap: persona?.estimatedHandicap || 18,
      golfBag: golfBag,
      clubDistances: clubDistances,
      hasCompletedOnboarding: true,
      updatedAt: new Date(),
    };
    
    console.log('📝 Updated profile after onboarding:', updatedProfile);
    console.log('📊 Golf bag has', golfBag.length, 'clubs');
    console.log('📊 Golf bag clubs:', golfBag.map(c => c.type).join(', '));
    console.log('📏 Club distances:', clubDistances.length, 'distances calibrated');
    console.log('🔍 Golf bag with distances:', golfBag.map(c => `${c.type}: ${c.distance}y`).join(', '));
    console.log('🔍 About to save to Supabase:', {
      userId: enforcedUserId,
      golfBagLength: golfBag.length,
      clubDistancesLength: clubDistances.length,
      hasCompletedOnboarding: true,
      averageScore: updatedProfile.averageScore,
      height: updatedProfile.height,
      weight: updatedProfile.weight,
      age: updatedProfile.age,
      ballFlight: updatedProfile.ballFlight,
      swingTempo: updatedProfile.swingTempo
    });
    
    // Update state first
    setUserProfile(updatedProfile);

    // Close modals immediately
    setShowOnboarding(false);
    setShowAuthModal(false);
    setHasCompletedOnboarding(true);
    void recordProductEvent('ONBOARDING_COMPLETED');
    if (golfBag.length > 0) void recordProductEvent('GOLF_BAG_CREATED');
    if (clubDistances.length > 0) void recordProductEvent('DISTANCES_COMPLETED');
    void recordActivationIfEligible({
      hasCompletedOnboarding: true,
      golfBag,
      clubDistances,
    });

    console.log('💾 Saving profile to Supabase...');

    // CRITICAL: Save to localStorage BEFORE Supabase to prevent email confirmation data loss
    const persistentBackupKey = `PERSISTENT_PROFILE_${enforcedUserId}`;
    const backupData = {
      golfBag: updatedProfile.golfBag,
      clubDistances: updatedProfile.clubDistances,
      height: updatedProfile.height,
      weight: updatedProfile.weight,
      age: updatedProfile.age,
      averageScore: updatedProfile.averageScore,
      ballFlight: updatedProfile.ballFlight,
      swingTempo: updatedProfile.swingTempo,
      swingSpeed: updatedProfile.swingSpeed,
      swingSpeedRange: updatedProfile.swingSpeedRange,
      skillLevel: updatedProfile.skillLevel,
      playingExperience: updatedProfile.playingExperience,
      courseFrequency: updatedProfile.courseFrequency,
      golfPersona: updatedProfile.golfPersona,
      golfGoals: updatedProfile.golfGoals,
      currentIssues: updatedProfile.currentIssues,
      preferredUnits: updatedProfile.preferredUnits,
      firstName: updatedProfile.firstName,
      lastName: updatedProfile.lastName,
      email: updatedProfile.email,
      handicap: updatedProfile.handicap,
      savedAt: new Date().toISOString()
    };

    // Save to THREE different localStorage keys for redundancy
    localStorage.setItem(persistentBackupKey, JSON.stringify(backupData));
    localStorage.setItem(`quizBackup_${enforcedUserId}`, JSON.stringify(backupData));
    localStorage.setItem(`onboarding_${enforcedUserId}`, JSON.stringify(backupData));
    
    console.log('💾 Profile backed up to localStorage (3 copies):', {
      golfBagLength: updatedProfile.golfBag?.length || 0,
      clubDistancesLength: updatedProfile.clubDistances?.length || 0
    });

    // Save to Supabase - FORCE save regardless of session status
    try {
      console.log('💾 Attempting to save to Supabase...');
      const saveResult = await handleProfileUpdate(updatedProfile);
      
      if (!saveResult) {
        console.error('❌ SAVE FAILED - handleProfileUpdate returned false');
      } else {
        console.log('✅ Profile saved successfully');
      }
      
      // Verify the save with retry logic
      console.log('🔍 Verifying save with enhanced retry...');
      let verification: any = null;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries) {
        const currentRetry = retryCount; // Capture for closure
        await new Promise(resolve => setTimeout(resolve, 2000 + (currentRetry * 1000))); // Increase delay with each retry
        
        verification = await profileService.verifyProfileSave(enforcedUserId);
        console.log(`🔍 Save verification attempt ${currentRetry + 1}:`, verification);

        if (verification.success) {
          console.log('✅✅✅ PROFILE DATA VERIFIED IN DATABASE!');
          break;
        } else {
          console.warn(`⚠️ Verification attempt ${currentRetry + 1} failed:`, verification.issues);
          retryCount++;
          
          if (retryCount < maxRetries) {
            console.log(`🔄 Retrying verification in ${2 + retryCount} seconds...`);
          }
        }
      }

      if (!verification?.success) {
        console.error('❌ CRITICAL: Data not saved completely to Supabase after all retries!');
        console.error('Issues:', verification?.issues);
        console.error('💾 Data is preserved in localStorage - recovery will trigger on next load');
      }

      console.log('✅ Profile save process completed');

      // Force reload profile from Supabase to ensure UI updates immediately
      // Only reload if verification succeeded
      if (verification?.success) {
        console.log('🔄 Force reloading profile to update UI...');
        await loadUserProfileFromSupabase(enforcedUserId);
      } else {
        console.log('⚠️ Skipping profile reload due to verification failure - will rely on localStorage recovery');
      }

      // Email should already be confirmed before quiz - no need to show confirmation screen again
      console.log('✅ Quiz completed - email was already confirmed');

    } catch (error) {
      console.error('❌ Error saving profile:', error);
      console.error('💾 Data is safe in localStorage backups');
    }

    // Show post-quiz success summary before navigating
    setQuizCompleteSummary({
      clubCount: golfBag.length,
      distanceCount: clubDistances.length,
    });
  };

  const handleOnboardingSkip = () => {
    setShowOnboarding(false);
    const skippedAt = new Date();
    const skipPatch = onboardingSkipProfilePatch(skippedAt);
    if (userProfile) {
      setUserProfile({ ...userProfile, ...skipPatch });
    }
    if (authUser?.id) {
      void supabase
        .from('profiles')
        .update(onboardingSkipDatabasePatch(skippedAt))
        .eq('id', authUser.id);
    }
  };

  const handleOnboardingClose = () => {
    setShowOnboarding(false);
  };

  const handleOpenOnboarding = () => {
    // Force quiz to reset to beginning when retaking
    setShowOnboarding(false);
    setTimeout(() => {
      setShowOnboarding(true);
      if (authUser) {
        void recordProductEvent('ONBOARDING_STARTED');
      }
    }, 100);
  };

  const handleCaptureComplete = (profile: UserProfileType) => {
    console.log('handleCaptureComplete called with profile:', profile);
    setUserProfile(profile);
    setShowCaptureForm(false);
    setShowAuthModal(false); // Also close auth modal

    // Execute the pending action if any
    if (pendingAction) {
      console.log('Executing pending action after capture:', pendingAction);
      if (pendingAction.startsWith('/')) {
        window.location.href = pendingAction;
      } else {
        console.log(`Executing action: ${pendingAction}`);
      }
      setPendingAction(null);
    } else {
      console.log('No pending action to execute after capture');
    }
  };



  useEffect(() => {
    // DISABLED: Auto-cleanup was too aggressive and interfering with new signups
    // Only use manual cleanup tool at /clear-session.html if needed
    // autoCleanupStaleSession().catch(console.error);
    
    // Set up Supabase auth state listener
    const getSession = async () => {
      try {
        console.log('🔍 Checking for session or auth token...');
        
        // This automatically handles email confirmation tokens from URL
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('❌ Session error:', error);
          setLoading(false);
          return;
        }

        if (session?.user) {
          console.log('✅ Session found:', session.user.email);

          const authUser: AuthUser = {
            id: session.user.id,
            email: session.user.email!,
            firstName: session.user.user_metadata.first_name || 'User',
            lastName: session.user.user_metadata.last_name || 'Name',
            createdAt: new Date(session.user.created_at),
            isAuthenticated: true,
          };

          setAuthUser(authUser);

          // Load user profile
          await loadUserProfileFromSupabase(session.user.id);
          
          // Check if we should show onboarding quiz after email confirmation
          const shouldShowOnboarding = localStorage.getItem(`show_onboarding_${session.user.id}`);
          if (shouldShowOnboarding === 'true') {
            console.log('📝 Opening onboarding quiz (flagged after email confirmation)');
            localStorage.removeItem(`show_onboarding_${session.user.id}`);
            setShowOnboarding(true);
          }
        } else {
          console.log('ℹ️ No session found - clearing auth state');
          // Clear auth state if no valid session
          setAuthUser(null);
          setUserProfile(null);
        }
      } catch (error) {
        console.error('💥 Session loading error:', error);
      } finally {
        setLoading(false);
      }
    };

    // Add timeout to prevent infinite loading (longer for mobile networks)
    const loadingTimeout = setTimeout(() => {
      console.log('Loading timeout - setting loading to false');
      setLoading(false);
    }, 2500); // Reduce splash wait to improve perceived load time

    getSession().finally(() => {
      clearTimeout(loadingTimeout);
    });

    // Track whether SIGNED_IN already loaded the profile to avoid duplicate loads
    let profileLoadedBySignIn = false;

    // Listen for auth changes (including email confirmation)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔔 Auth state change:', event, session?.user?.email);

        // Detect email confirmation and trigger sync
        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
          void supabase.rpc('record_authenticated_session');
        }

        if (event === 'SIGNED_IN' && session?.user) {
          console.log('✅ SIGNED_IN event - syncing and loading profile...');
          await syncLocalStorageToSupabase(session.user.id, session);
          
          setShowEmailConfirmation(false);
          
          const profile = await loadUserProfileFromSupabase(session.user.id);
          profileLoadedBySignIn = true;
          
          if (profile && !profile.hasCompletedOnboarding) {
            console.log('📝 Email confirmed - flagging to show onboarding quiz');
            localStorage.setItem(`show_onboarding_${session.user.id}`, 'true');
          }

          // Handle onboarding check inline (no second load needed)
          const shouldShowOnboarding = localStorage.getItem(`show_onboarding_${session.user.id}`);
          if (shouldShowOnboarding === 'true' && profile && !profile.hasCompletedOnboarding) {
            console.log('📝 Opening onboarding quiz (flagged after email confirmation)');
            localStorage.removeItem(`show_onboarding_${session.user.id}`);
            setTimeout(() => setShowOnboarding(true), 500);
          }
        }

        if (session?.user) {
          const authUser: AuthUser = {
            id: session.user.id,
            email: session.user.email!,
            firstName: session.user.user_metadata.first_name || 'User',
            lastName: session.user.user_metadata.last_name || 'Name',
            createdAt: new Date(session.user.created_at),
            isAuthenticated: true,
          };

          setAuthUser(prev => prev?.id === authUser.id ? prev : authUser);

          // Skip if SIGNED_IN handler already loaded the profile this cycle
          if (profileLoadedBySignIn) {
            profileLoadedBySignIn = false;
            console.log('⏭️ Skipping redundant profile load (already handled by SIGNED_IN)');
          } else if (!userProfile || userProfile.id !== session.user.id) {
            console.log('🔄 Loading profile for authenticated user:', session.user.email);
            const loadedProfile = await loadUserProfileFromSupabase(session.user.id);
            
            const shouldShowOnboarding = localStorage.getItem(`show_onboarding_${session.user.id}`);
            if (shouldShowOnboarding === 'true' && loadedProfile && !loadedProfile.hasCompletedOnboarding) {
              console.log('📝 Opening onboarding quiz (flagged after email confirmation)');
              localStorage.removeItem(`show_onboarding_${session.user.id}`);
              setTimeout(() => setShowOnboarding(true), 500);
            }

            if (loadedProfile) {
              console.log('✅ Profile loaded successfully:', {
                id: loadedProfile.id,
                hasCompletedOnboarding: loadedProfile.hasCompletedOnboarding,
                golfBagLength: loadedProfile.golfBag?.length || 0,
                clubDistancesLength: loadedProfile.clubDistances?.length || 0
              });
            } else {
              console.log('ℹ️ No profile found for user - this is normal for new users');
            }
          }
        } else {
          console.log('🔄 No session found - clearing auth state');
          setAuthUser(null);
          setUserProfile(null);
        }
      }
    );

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show platform tour when user just completed quiz and clicked "Review My Bag"
  useEffect(() => {
    if (userProfile?.id && sessionStorage.getItem('showPlatformTour') === 'true') {
      sessionStorage.removeItem('showPlatformTour');
      setShowProductTour(true);
    }
  }, [userProfile?.id]);

  // Separate function for explicitly recalculating distances (when swing speed changes)
  // Currently unused but preserved for future profile editing feature
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSwingSpeedRecalculation = async (profile: UserProfileType) => {
    const updatedProfile = updateClubDistances(profile);
    await handleProfileUpdate(updatedProfile);
  };

  const handleProfileUpdate = async (profile: UserProfileType): Promise<boolean> => {
    // VALIDATE PROFILE DATA BEFORE SAVING
    if (!profile.id) {
      console.error('❌ PROFILE UPDATE ERROR: No user ID provided');
      return false;
    }

    if (!profile.firstName || !profile.lastName || !profile.email) {
      console.error('❌ PROFILE UPDATE ERROR: Missing required fields');
      return false;
    }

    // Ensure derived fields are in sync before saving
    // DON'T auto-recalculate distances - preserve user's quiz-calibrated values
    const updatedProfile = { ...profile };
    
    // If averageScore is set, ensure handicap/persona/swingSpeed are derived from it
    if (updatedProfile.averageScore && updatedProfile.averageScore > 0) {
      const persona = getPersonaFromScore(updatedProfile.averageScore);
      updatedProfile.handicap = estimateHandicapFromScore(updatedProfile.averageScore);
      updatedProfile.golfPersona = persona.id;
      if (!updatedProfile.swingSpeed) {
        updatedProfile.swingSpeed = estimateSwingSpeed(updatedProfile.averageScore, updatedProfile.age || 35);
      }
    }

    // STEP 1: Save to localStorage IMMEDIATELY — this guarantees the user's changes
    // are preserved no matter what happens with the server call.
    saveUserProfile(updatedProfile);
    const persistentBackupKey = `PERSISTENT_PROFILE_${profile.id}`;
    localStorage.setItem(persistentBackupKey, JSON.stringify({
      ...updatedProfile,
      savedAt: new Date().toISOString()
    }));

    // STEP 2: Update local state immediately so the UI reflects the change.
    // The user should never wait for or be blocked by the server round-trip.
    setUserProfile(updatedProfile);

    // STEP 3: Save to Supabase with automatic retry (up to 3 attempts).
    // If this fails, the data is already safe in localStorage and state.
    const MAX_RETRIES = 3;
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        // On retry attempts, refresh the session in case it expired
        if (attempt > 1) {
          console.log(`🔄 Retry attempt ${attempt}/${MAX_RETRIES} — refreshing session...`);
          await supabase.auth.refreshSession();
          // Small delay between retries
          await new Promise(resolve => setTimeout(resolve, 500 * attempt));
        }

        const savedProfile = await profileService.upsertProfile(updatedProfile);

        // Server save succeeded — update state and localStorage with server-confirmed data
        setUserProfile(savedProfile);
        saveUserProfile(savedProfile);
        console.log('✅ Profile saved to server successfully');
        return true;

      } catch (error) {
        console.error(`❌ Server save attempt ${attempt}/${MAX_RETRIES} failed:`, error);
        
        // If it's an auth error (session expired), try refreshing on next attempt
        const errMsg = error instanceof Error ? error.message : String(error);
        if (errMsg.includes('Not authenticated') || errMsg.includes('JWT') || errMsg.includes('401')) {
          console.log('🔑 Auth error detected — will refresh session on retry');
        }

        if (attempt === MAX_RETRIES) {
          console.error('❌ All server save attempts failed. Data is safe in localStorage.');
        }
      }
    }

    // Server save failed after all retries. Data is safe locally but caller
    // should know so it can display appropriate feedback.
    return false;
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background:
            "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 25%, #f1f5f9 50%, #e0f2fe 75%, #f0fdf4 100%)",
        }}
      >
        <div className="text-center animate-fade-in">
          <div
            style={{
              width: "4rem",
              height: "4rem",
              margin: "0 auto 1rem",
              background:
                "linear-gradient(135deg, var(--primary-500), var(--primary-600))",
              borderRadius: "1.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 10px 30px rgba(34, 197, 94, 0.3)",
              animation: "float 2s ease-in-out infinite",
            }}
          >
            <span
              style={{ color: "white", fontWeight: "bold", fontSize: "1.5rem" }}
            >
              CQ
            </span>
          </div>
          <h2 className="gradient-text text-2xl font-bold mb-2">ClubIQ</h2>
          <p style={{ color: "var(--gray-600)", fontSize: "1rem" }}>
            Loading your golf assistant...
          </p>
          <div
            style={{
              width: "3rem",
              height: "3rem",
              margin: "1rem auto",
              border: "3px solid var(--gray-200)",
              borderTop: "3px solid var(--primary-500)",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
        </div>
      </div>
    );
  }


  return (
    <HelmetProvider>
    <Router>
      <ScrollToTop />
      <div className="min-h-screen w-full overflow-x-hidden m-0 p-0">
        <Navigation
          userProfile={userProfile}
          authUser={authUser}
          onSignOut={handleSignOut}
          onOpenAuthModal={handleOpenAuthModal}
          onActionRequiringInfo={handleActionRequiringInfo}
          onTakeTour={() => setShowProductTour(true)}
        />
        <DataNotice />
        {authUser && userProfile && canResumeOnboarding(userProfile) && !showOnboarding && (
          <div className="mx-auto max-w-5xl px-4 pt-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-sm text-amber-900">
                {userProfile.onboardingSkippedAt
                  ? 'Your golf profile is unfinished. You can keep using ClubIQ and finish the quiz whenever you are ready.'
                  : 'Finish your golf profile so ClubIQ can personalize your bag and distances.'}
              </p>
              <button
                type="button"
                onClick={handleOpenOnboarding}
                className="shrink-0 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
              >
                Resume onboarding
              </button>
            </div>
          </div>
        )}
        <main
          className="max-sm:pb-28 sm:pb-10"
          style={{
            paddingTop: "4rem",
          }}
        >
          <div className="w-full p-0 m-0">
            <Routes>
              <Route
                path="/"
                element={
                  loading ? (
                    <div className="flex items-center justify-center min-h-screen">
                      <div className="text-center">
                        <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading...</p>
                      </div>
                    </div>
                  ) : authUser && userProfile ? (
                    <Navigate to="/my-golf-bag" replace />
                  ) : (
                    <Dashboard
                      userProfile={userProfile}
                      authUser={authUser}
                      onActionRequiringInfo={handleActionRequiringInfo}
                      onOpenAuthModal={handleOpenAuthModal}
                      onOpenContactForm={() => setShowContactForm(true)}
                      onOpenOnboarding={handleOpenOnboarding}
                    />
                  )
                }
              />
              
              {/* Marketing Homepage - Accessible to all users */}
              <Route
                path="/home"
                element={
                  <Dashboard
                    userProfile={userProfile}
                    authUser={authUser}
                    onActionRequiringInfo={handleActionRequiringInfo}
                    onOpenAuthModal={handleOpenAuthModal}
                    onOpenContactForm={() => setShowContactForm(true)}
                    onOpenOnboarding={handleOpenOnboarding}
                  />
                }
              />

              {/* Password Reset Route - Public */}
              <Route
                path="/reset-password"
                element={<ResetPassword />}
              />

              {/* Public Footer Pages */}
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/partner/invite/:token" element={<PartnerInvite />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/internal/growth" element={<GrowthOps />} />
              
              {/* Public Bag View */}
              <Route path="/bag/:bagId" element={<PublicBagView />} />
              
              {/* Bag Comparison */}
              <Route 
                path="/compare" 
                element={
                  <ProtectedRoute
                    isAuthenticated={!!authUser}
                    openAuthModal={handleOpenAuthModal}
                  >
                    <BagComparison userProfile={userProfile} />
                  </ProtectedRoute>
                } 
              />
              
              {/* Progress Dashboard */}
              <Route 
                path="/progress" 
                element={
                  <ProtectedRoute
                    isAuthenticated={!!authUser}
                    openAuthModal={handleOpenAuthModal}
                  >
                    <ProgressDashboard 
                      userProfile={userProfile} 
                      onProfileUpdate={handleProfileUpdate}
                    />
                  </ProtectedRoute>
                } 
              />
              
              {/* Find a Fitter */}
              <Route path="/find-fitter" element={<FindFitter />} />

              {/* TWO-PILLAR SYSTEM */}
              <Route
                path="/my-golf-bag"
                element={
                  <ProtectedRoute
                    isAuthenticated={!!authUser}
                    openAuthModal={handleOpenAuthModal}
                  >
                    <MyGolfBag
                      userProfile={userProfile}
                      onProfileUpdate={handleProfileUpdate}
                    />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/club-optimizer"
                element={
                  <ProtectedRoute
                    isAuthenticated={!!authUser}
                    openAuthModal={handleOpenAuthModal}
                  >
                    <ClubOptimizerHub
                      userProfile={userProfile}
                      onProfileUpdate={handleProfileUpdate}
                    />
                  </ProtectedRoute>
                }
              />

              {/* REDIRECTS - Old routes redirect to Optimizer sub-tabs */}
              <Route path="/shot-advisor" element={<Navigate to="/club-optimizer?tab=shot-advisor" replace />} />
              <Route path="/swing-analyzer" element={<Navigate to="/club-optimizer?tab=swing-tips" replace />} />
              <Route path="/ball-matching" element={<Navigate to="/club-optimizer?tab=ball-match" replace />} />
              <Route path="/equipment-fitting" element={<Navigate to="/club-optimizer?tab=equipment-fitting" replace />} />
              <Route path="/distance-calculator" element={<Navigate to="/club-optimizer?tab=gapping" replace />} />
              <Route path="/encyclopedia" element={<Navigate to="/club-optimizer?tab=health-check" replace />} />

              <Route
                path="/profile"
                element={
                  <ProtectedRoute
                    isAuthenticated={!!authUser}
                    openAuthModal={handleOpenAuthModal}
                  >
                    <UserProfile
                      userProfile={userProfile}
                      onProfileUpdate={handleProfileUpdate}
                      onSignOut={handleSignOut}
                      onOpenOnboarding={() => setShowOnboarding(true)}
                    />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/distances"
                element={
                  <ProtectedRoute
                    isAuthenticated={!!authUser}
                    openAuthModal={handleOpenAuthModal}
                  >
                    <Distances
                      userProfile={userProfile}
                      onProfileUpdate={handleProfileUpdate}
                    />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/recommendations"
                element={
                  <ProtectedRoute
                    isAuthenticated={!!authUser}
                    openAuthModal={handleOpenAuthModal}
                  >
                    <Recommendations userProfile={userProfile} />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/beginner-builder"
                element={
                  <ProtectedRoute
                    isAuthenticated={!!authUser}
                    openAuthModal={handleOpenAuthModal}
                  >
                    <BeginnerBuilder
                      userProfile={userProfile}
                      onProfileUpdate={handleProfileUpdate}
                    />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
        </main>

        {/* Welcome Overlay */}
        {showWelcomeOverlay && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 100,
              backdropFilter: "blur(5px)",
            }}
          >
            <div
              style={{
                background: "white",
                padding: "3rem",
                borderRadius: "2rem",
                maxWidth: "500px",
                textAlign: "center",
                boxShadow: "0 25px 50px rgba(0, 0, 0, 0.3)",
                position: "relative",
                animation: "fadeIn 0.5s ease-out",
              }}
            >
              <button
                onClick={() => setShowWelcomeOverlay(false)}
                style={{
                  position: "absolute",
                  top: "1rem",
                  right: "1rem",
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                  color: "#666",
                }}
              >
                ×
              </button>

              <div
                style={{
                  width: "4rem",
                  height: "4rem",
                  margin: "0 auto 1.5rem",
                  background: "linear-gradient(135deg, var(--primary-500), var(--primary-600))",
                  borderRadius: "1.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 10px 30px rgba(34, 197, 94, 0.3)",
                }}
              >
                <span style={{ color: "white", fontWeight: "bold", fontSize: "1.5rem" }}>
                  CQ
                </span>
              </div>

              <h2 style={{ fontSize: "2rem", fontWeight: "bold", marginBottom: "1rem", color: "#1e293b" }}>
                Welcome to ClubIQ! 🎯
              </h2>

              <p style={{ fontSize: "1.1rem", color: "#64748b", marginBottom: "2rem", lineHeight: "1.6" }}>
                Your AI-powered golf assistant for better shots and lower scores.
                <br /><br />
                <strong>Ready to get started?</strong>
              </p>

              <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
                <button
                  onClick={() => {
                    setShowWelcomeOverlay(false);
                    setAuthModalMode("signup");
                    setShowAuthModal(true);
                  }}
                  style={{
                    background: "linear-gradient(135deg, var(--primary-500), var(--primary-600))",
                    color: "white",
                    border: "none",
                    padding: "1rem 2rem",
                    borderRadius: "1rem",
                    fontSize: "1rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    boxShadow: "0 10px 30px rgba(34, 197, 94, 0.3)",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 15px 40px rgba(34, 197, 94, 0.4)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 10px 30px rgba(34, 197, 94, 0.3)";
                  }}
                >
                  Get Started
                </button>

                <button
                  onClick={() => setShowWelcomeOverlay(false)}
                  style={{
                    background: "transparent",
                    color: "#64748b",
                    border: "2px solid #e2e8f0",
                    padding: "1rem 2rem",
                    borderRadius: "1rem",
                    fontSize: "1rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = "#f8fafc";
                    e.currentTarget.style.borderColor = "#cbd5e1";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.borderColor = "#e2e8f0";
                  }}
                >
                  Explore First
                </button>
              </div>
            </div>
          </div>
        )}

        {/* User Capture Form */}
            <UserCaptureForm
              isOpen={showCaptureForm}
              onComplete={handleCaptureComplete}
              onCancel={() => setShowCaptureForm(false)}
            />

        {/* Authentication Modal */}
        <AuthModal
          isOpen={showAuthModal}
          mode={authModalMode}
          onClose={handleCloseAuthModal}
          onSwitchMode={handleSwitchAuthMode}
          onSignIn={handleSignIn}
          onSignUp={handleSignUp}
        />

        {/* Contact Form */}
        <ContactForm
          isOpen={showContactForm}
          onClose={() => setShowContactForm(false)}
        />

        {/* Onboarding Wizard */}
        <OnboardingWizard
          isOpen={showOnboarding}
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
          onClose={handleOnboardingClose}
        />

        {/* Email Confirmation Screen */}
        <EmailConfirmationScreen
          isOpen={showEmailConfirmation}
          userEmail={authUser?.email || ""}
          onClose={() => setShowEmailConfirmation(false)}
        />

        {/* Quiz Complete Summary */}
        {quizCompleteSummary && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white text-center">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </div>
                <h2 className="text-2xl font-bold">You're All Set!</h2>
                <p className="text-green-100 mt-1">Your golf profile is ready</p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-emerald-50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-emerald-700">{quizCompleteSummary.clubCount}</div>
                    <div className="text-sm text-emerald-600 font-medium">Clubs in Bag</div>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-blue-700">{quizCompleteSummary.distanceCount}</div>
                    <div className="text-sm text-blue-600 font-medium">Distances Set</div>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                  <p className="text-amber-800 text-sm font-medium">
                    <strong>Next step:</strong> Review your clubs and confirm your carry distances. Accurate distances power every recommendation.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setQuizCompleteSummary(null);
                    sessionStorage.setItem('showPlatformTour', 'true');
                    window.location.href = '/my-golf-bag';
                  }}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 px-6 rounded-xl font-bold text-lg hover:shadow-lg transition-all"
                >
                  Review My Bag →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Onboarding Walkthrough */}
        {isAuthenticated && userProfile && (
          <OnboardingWalkthrough
            userProfile={userProfile}
            onComplete={() => setShowProductTour(false)}
            forceShow={showProductTour}
          />
        )}

        {/* AI Chat Widget */}
        <AskClubIQ userProfile={userProfile} />

        {/* PWA Install Prompt */}
        <InstallPrompt />
      </div>
    </Router>
    </HelmetProvider>
  );
}

export default App;
